import { useEffect, useRef, useState } from "react";
import { CLUB_COUNTRIES } from "../../constants/countries";
import {
  confirmNewcomerCardPayment,
  getNewcomerDossier,
  getPublicNewcomerDossier,
  newcomerFormPdfUrl,
  recordNewcomerPayment,
  saveNewcomerFacts,
  saveNewcomerLicence,
  saveNewcomerPaymentSettings,
  signNewcomerDossier,
  startNewcomerCardPayment,
  uploadNewcomerDocument,
  verifyNewcomerDocument,
} from "../funtionsBD/Mainservice";
import "./NewcomerDossierPanel.css";

const AFFILIATION_INPUTS = [
  "licence_type",
  "birth_name",
  "usage_first_name",
  "sex",
  "birth_day",
  "birth_month",
  "birth_year",
  "matricule",
  "nationality",
  "residence_country",
  "postal_code",
  "locality",
  "house_number",
  "street",
  "email",
  "phone",
  "previous_member",
  "previous_club",
  "previous_federation",
  "previous_year_out",
  "tutor_name",
  "tutor_relation",
  "tutor_email",
];

const FIELD_META = {
  licence_type: { label: "Type de licence", options: [["joueur", "Joueur"], ["loisir", "Loisir"]] },
  birth_name: { label: "NOM (de naissance)" },
  usage_first_name: { label: "Prénom (d'usage)" },
  sex: { label: "Sexe", options: [["", "—"], ["M", "M"], ["F", "F"]] },
  birth_date: { label: "Date de naissance", type: "date" },
  birth_day: { label: "Jour de naissance", inputMode: "numeric" },
  birth_month: { label: "Mois de naissance", inputMode: "numeric" },
  birth_year: { label: "Année de naissance", inputMode: "numeric" },
  matricule: { label: "Matricule", inputMode: "numeric" },
  nationality: { label: "Nationalité" },
  residence_country: { label: "Pays" },
  postal_code: { label: "Code postal" },
  locality: { label: "Localité" },
  house_number: { label: "N° maison" },
  street: { label: "Rue" },
  email: { label: "E-mail", type: "email" },
  phone: { label: "N° tél.", type: "tel" },
  previous_club: { label: "Club précédent" },
  previous_federation: { label: "Fédération du club précédent" },
  previous_year_out: { label: "Année de sortie", inputMode: "numeric" },
  tutor_name: { label: "Tuteur — nom et prénom" },
  tutor_relation: { label: "Relation" },
  tutor_email: { label: "E-mail du tuteur", type: "email" },
};

const PREVIOUS_KEYS = ["previous_member", "previous_club", "previous_federation", "previous_year_out"];

export function affiliationFromForm(form) {
  const data = new FormData(form);
  const affiliation = {};
  const birthDate = form.elements.namedItem("birth_date");
  if (birthDate) {
    const [year, month, day] = String(birthDate.value || "").split("-");
    affiliation.birth_year = year || "";
    affiliation.birth_month = month || "";
    affiliation.birth_day = day || "";
  }
  AFFILIATION_INPUTS.forEach((key) => {
    if (!form.elements.namedItem(key)) return;
    let value = String(data.get(key) || "").trim();
    if ((key === "birth_day" || key === "birth_month") && /^\d{1,2}$/.test(value)) {
      value = value.padStart(2, "0");
    }
    affiliation[key] = value;
  });
  if (affiliation.previous_member === "no") {
    affiliation.previous_club = "";
    affiliation.previous_federation = "";
    affiliation.previous_year_out = "";
  }
  return affiliation;
}

function previousAnswer(affiliation) {
  if (affiliation?.previous_member === "yes" || affiliation?.previous_member === "no") {
    return affiliation.previous_member;
  }
  return affiliation?.previous_club ? "yes" : "";
}

function PreviousMembershipFields({ affiliation, required }) {
  const [answer, setAnswer] = useState(previousAnswer(affiliation));
  return (
    <div className="nd-previous">
      <label>
        Already a member of another club or federation?
        <select
          name="previous_member"
          value={answer}
          required={required}
          onChange={(event) => setAnswer(event.target.value)}
        >
          <option value="">Choose</option>
          <option value="no">No — leave those lines blank</option>
          <option value="yes">Yes</option>
        </select>
      </label>
      {answer === "yes" ? (
        <>
          <label>
            Club précédent
            <input name="previous_club" defaultValue={affiliation.previous_club || ""} required={required} />
          </label>
          <label>
            Fédération du club précédent
            <input name="previous_federation" defaultValue={affiliation.previous_federation || ""} />
          </label>
          <label>
            Année de sortie
            <input name="previous_year_out" inputMode="numeric" defaultValue={affiliation.previous_year_out || ""} />
          </label>
        </>
      ) : null}
    </div>
  );
}

export function AffiliationFields({ affiliation, keys, required }) {
  let previousRendered = false;
  return keys.map((key) => {
    if (PREVIOUS_KEYS.includes(key)) {
      if (previousRendered) return null;
      previousRendered = true;
      return (
        <PreviousMembershipFields
          key="previous-membership"
          affiliation={affiliation}
          required={required}
        />
      );
    }
    const meta = FIELD_META[key];
    if (!meta) return null;
    const value = key === "birth_date"
      ? [affiliation.birth_year, affiliation.birth_month, affiliation.birth_day].every(Boolean)
        ? `${affiliation.birth_year}-${affiliation.birth_month}-${affiliation.birth_day}`
        : ""
      : affiliation[key] || "";
    if (meta.options) {
      return (
        <label key={key}>
          {meta.label}
          <select name={key} defaultValue={value || (key === "licence_type" ? "joueur" : "")} required={required && !["licence_type", "previous_club", "previous_federation", "previous_year_out"].includes(key)}>
            {meta.options.map(([optionValue, optionLabel]) => (
              <option key={optionValue || "empty"} value={optionValue}>{optionLabel}</option>
            ))}
          </select>
        </label>
      );
    }
    return (
      <label key={key}>
        {meta.label}
        <input
          name={key}
          type={meta.type || "text"}
          inputMode={meta.inputMode}
          defaultValue={value}
          required={required && !["previous_club", "previous_federation", "previous_year_out"].includes(key)}
        />
      </label>
    );
  });
}

export function flttGapFieldKeys(missing) {
  const birthKeys = ["birth_day", "birth_month", "birth_year"];
  const gapKeys = (missing || []).map((item) => item.key);
  if (!birthKeys.some((key) => gapKeys.includes(key))) return gapKeys;
  return ["birth_date", ...gapKeys.filter((key) => !birthKeys.includes(key))];
}

function printAffiliation(form, { run, save, pdfUrl }) {
  if (!form) {
    window.open(pdfUrl, "_blank", "noopener");
    return;
  }
  const affiliation = affiliationFromForm(form);
  const popup = window.open("about:blank", "_blank");
  run(async () => {
    try {
      const next = await save(affiliation);
      if (next?.data?.affiliation_missing?.length) {
        if (popup) popup.close();
        return next;
      }
      if (popup) popup.location.href = pdfUrl;
      return next;
    } catch (err) {
      if (popup) popup.close();
      throw err;
    }
  });
}

function euros(cents) {
  const value = Number(cents) || 0;
  return `${(value / 100).toFixed(2)} €`;
}

function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const strokesRef = useRef([]);
  const currentRef = useRef(null);

  const paint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#14241c";
    ctx.lineWidth = 2.4 * (window.devicePixelRatio || 1);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const poly of strokesRef.current) {
      if (poly.length < 2) continue;
      ctx.beginPath();
      poly.forEach((point, index) => {
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(rect.height * ratio));
      paint();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pointFrom = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const source = event.touches ? event.touches[0] : event;
    return {
      x: Math.min(1, Math.max(0, (source.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (source.clientY - rect.top) / rect.height)),
    };
  };

  const start = (event) => {
    event.preventDefault();
    currentRef.current = [pointFrom(event)];
    strokesRef.current = [...strokesRef.current, currentRef.current];
  };
  const move = (event) => {
    if (!currentRef.current) return;
    event.preventDefault();
    currentRef.current.push(pointFrom(event));
    paint();
  };
  const end = () => {
    currentRef.current = null;
    onChange(strokesRef.current);
  };

  return (
    <canvas
      ref={canvasRef}
      className="nd-pad"
      aria-label="Signature"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
    />
  );
}

export function NewcomerDossierPanel({
  mode = "public",
  token,
  dossierId,
  onCreated,
}) {
  const staff = mode === "staff";
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [strokes, setStrokes] = useState([]);
  const [signerName, setSignerName] = useState("");
  const [licence, setLicence] = useState("");
  const [bank, setBank] = useState(null);
  const [editingAffiliation, setEditingAffiliation] = useState(false);

  const load = async (next) => {
    const payload = next || (staff
      ? await getNewcomerDossier(dossierId)
      : await getPublicNewcomerDossier(token));
    setDossier(payload.data);
    setSignerName(
      [payload.data?.person?.first_name, payload.data?.person?.last_name].filter(Boolean).join(" ")
    );
    setLicence(payload.data?.licence_number || "");
    setBank({
      bank_account_holder: payload.data?.bank?.account_holder || "",
      bank_iban: payload.data?.bank?.iban || "",
      bank_bic: payload.data?.bank?.bic || "",
      membership_fee_cents: payload.data?.fees?.membership_cents || 0,
      session_fee_cents: payload.data?.fees?.session_cents || 0,
      affiliation_signatory_name: payload.data?.club_signatory?.name || "",
      affiliation_signatory_role: payload.data?.club_signatory?.role || "",
      club_id: payload.data?.club_id,
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await load();
      } catch (err) {
        if (!cancelled) setError(err?.message || "Could not open this dossier");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, dossierId, staff]);

  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(window.location.search);
    const paymentId = params.get("payment_id");
    if (params.get("paid") !== "1" || !paymentId) return;
    confirmNewcomerCardPayment(token, paymentId)
      .then((payload) => setDossier(payload.data))
      .catch((err) => setError(err?.message || "The card payment is not completed yet"));
  }, [token]);

  const run = async (work) => {
    setBusy(true);
    setError("");
    try {
      const payload = await work();
      if (payload?.data) setDossier(payload.data);
      else await load();
    } catch (err) {
      setError(err?.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!dossier) {
    return (
      <div className={staff ? "" : "nd-page"}>
        <div className="nd-sheet">
          <p>{error || "Loading the registration…"}</p>
        </div>
      </div>
    );
  }

  const fltt = dossier.federation_system === "FLTT";
  const pdfUrl = newcomerFormPdfUrl({
    token: token || dossier.access_token,
    dossierId: dossier.dossier_id,
  });
  const missing = dossier.affiliation_missing || [];
  const affiliationReady = fltt && missing.length === 0;
  const birthKeys = ["birth_day", "birth_month", "birth_year"];
  const gapKeys = missing.map((item) => item.key);
  const publicKeys = birthKeys.some((key) => gapKeys.includes(key))
    ? ["birth_date", ...gapKeys.filter((key) => !birthKeys.includes(key))]
    : gapKeys;
  const staffKeys = [
    "licence_type", "birth_name", "usage_first_name", "sex", "birth_date",
    "matricule", "nationality", "residence_country", "postal_code", "locality",
    "house_number", "street", "email", "phone",
    "previous_member",
    "tutor_name", "tutor_relation", "tutor_email",
  ].filter((key) => dossier.affiliation?.show_tutor || !key.startsWith("tutor_"));
  const showAffiliationForm = Boolean(dossier.affiliation) && (staff || !affiliationReady || editingAffiliation);
  const saveAffiliation = (affiliation) => saveNewcomerFacts({
    dossierId: dossier.dossier_id,
    token,
    facts: { affiliation },
  });

  const shell = (
    <article className="nd-sheet">
      <p className="nd-kicker">{dossier.club_name} · {dossier.federation_label}</p>
      <h1>{dossier.form?.title}</h1>
      <p className="nd-stamp">{dossier.form?.watermark}</p>
      <p className="nd-lead">
        {dossier.person?.first_name} {dossier.person?.last_name} · trial days {dossier.trial?.days_used}/{dossier.trial?.limit}
      </p>
      {error ? <p className="nd-error">{error}</p> : null}

      {!staff && fltt && affiliationReady && !editingAffiliation ? (
        <section className="nd-print">
          <h2>Print and sign</h2>
          <p className="nd-note">
            The FLTT form is already filled from the registration. Print it and sign the paper. The signature lines stay empty.
          </p>
          <dl className="nd-summary">
            <div><dt>NOM</dt><dd>{dossier.affiliation.birth_name}</dd></div>
            <div><dt>Prénom</dt><dd>{dossier.affiliation.usage_first_name}</dd></div>
            <div><dt>Matricule</dt><dd>{dossier.affiliation.matricule}</dd></div>
            <div><dt>Nationalité</dt><dd>{dossier.affiliation.nationality}</dd></div>
            <div><dt>Adresse</dt><dd>{[dossier.affiliation.house_number, dossier.affiliation.street, dossier.affiliation.postal_code, dossier.affiliation.locality].filter(Boolean).join(", ")}</dd></div>
            <div><dt>Téléphone</dt><dd>{dossier.affiliation.phone}</dd></div>
          </dl>
          <div className="nd-actions">
            <button type="button" disabled={busy} onClick={() => printAffiliation(null, { run, save: saveAffiliation, pdfUrl })}>
              Print and sign
            </button>
            <button type="button" className="nd-btn--quiet" onClick={() => setEditingAffiliation(true)}>
              Correct a detail
            </button>
          </div>
        </section>
      ) : null}

      {showAffiliationForm ? (
        <form
          className="nd-form"
          key={staff
            ? `staff-${dossier.affiliation.matricule}-${dossier.affiliation.phone}-${dossier.affiliation.birth_year}`
            : publicKeys.join("-")}
          onSubmit={(event) => {
            event.preventDefault();
            printAffiliation(event.currentTarget, { run, save: saveAffiliation, pdfUrl });
          }}
        >
          <h2>{staff ? "Demande d'affiliation FLTT" : "Details still needed for the FLTT form"}</h2>
          <p className="nd-note">
            {staff
              ? "These answers are printed on the form. Signature lines stay empty."
              : "Once these are saved, the only step left is to print the form and sign it."}
          </p>
          <AffiliationFields
            affiliation={dossier.affiliation}
            keys={staff ? staffKeys : publicKeys}
            required={!staff}
          />
          <div className="nd-actions">
            {staff ? (
              <button
                type="button"
                className="nd-btn--quiet"
                disabled={busy}
                onClick={(event) => {
                  const affiliation = affiliationFromForm(event.currentTarget.form);
                  run(() => saveAffiliation(affiliation));
                }}
              >
                Save
              </button>
            ) : null}
            <button type="submit" disabled={busy}>Print and sign</button>
            {editingAffiliation ? (
              <button type="button" className="nd-btn--quiet" onClick={() => setEditingAffiliation(false)}>
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      ) : null}

      {fltt ? (
        <form
          className="nd-form"
          key={`${dossier.facts?.eu_citizen}-${dossier.facts?.birth_country}-${dossier.facts?.parental_registration}`}
          onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            run(() => saveNewcomerFacts({
              dossierId: dossier.dossier_id,
              token,
              facts: {
                eu_citizen: data.get("eu_citizen") === "yes",
                birth_country: data.get("birth_country"),
                parental_registration: data.get("parental_registration") === "yes",
              },
            }));
          }}
        >
          <label>
            Citizen of the European Union
            <select name="eu_citizen" defaultValue={dossier.facts?.eu_citizen === false ? "no" : dossier.facts?.eu_citizen === true ? "yes" : ""}>
              <option value="">Choose</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label>
            Country of birth
            <select name="birth_country" defaultValue={dossier.facts?.birth_country || ""}>
              <option value="">Choose</option>
              {CLUB_COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>{country.name}</option>
              ))}
            </select>
          </label>
          <label>
            A parent is registering this child
            <select name="parental_registration" defaultValue={dossier.facts?.parental_registration ? "yes" : "no"}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </label>
          <div className="nd-actions">
            <button type="submit" disabled={busy}>Save these details</button>
          </div>
        </form>
      ) : null}

      <div className="nd-grid">
        {dossier.required_documents?.map((doc) => (
          <div className="nd-row" key={doc.kind}>
            <div>
              <strong>{doc.label}</strong>
              <span>{doc.verified ? "Verified by the club" : doc.uploaded ? "Received" : "Still needed"}</span>
            </div>
            <div className="nd-actions">
              <label className="nd-btn">
                Add photo
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (!file) return;
                    run(() => uploadNewcomerDocument({
                      dossierId: dossier.dossier_id,
                      token,
                      kind: doc.kind,
                      file,
                    }));
                  }}
                />
              </label>
              {staff && doc.uploaded && !doc.verified ? (
                <button
                  type="button"
                  className="nd-btn--quiet"
                  disabled={busy}
                  onClick={() => {
                    const match = dossier.documents?.find((row) => row.kind === doc.kind);
                    if (!match) return;
                    run(() => verifyNewcomerDocument(dossier.dossier_id, match.document_id));
                  }}
                >
                  Mark verified
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      <section className="nd-sign">
        <h2>Sign the registration</h2>
        <p className="nd-note">
          The printed FLTT form is the one the club sends. Sign that paper by hand. The pad below is only a prototype and is not printed on the form.
        </p>
        {(dossier.form?.lines || []).map((line) => (
          <p key={line} className="nd-note">{line}</p>
        ))}
        {dossier.signature ? (
          <p>Signed by {dossier.signature.signer_name} · {dossier.signature.payload_hash}</p>
        ) : (
          <>
            <label>
              Name of the person signing
              <input value={signerName} onChange={(event) => setSignerName(event.target.value)} />
            </label>
            <SignaturePad onChange={setStrokes} />
            <div className="nd-actions">
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => signNewcomerDossier({
                  token,
                  dossierId: dossier.dossier_id,
                  strokes,
                  signer_name: signerName,
                }))}
              >
                Sign
              </button>
            </div>
          </>
        )}
        {dossier.affiliation ? null : (
          <p>
            <a className="nd-btn" href={pdfUrl} target="_blank" rel="noreferrer">Open the form PDF</a>
          </p>
        )}
      </section>

      <section className="nd-bank">
        <h2>Payment</h2>
        <p className="nd-note">
          Transfer {euros(dossier.fees?.membership_cents)} for membership or {euros(dossier.fees?.session_cents)} per session after the three free days.
        </p>
        <p>Account holder: {dossier.bank?.account_holder || "Not set yet"}</p>
        <p>IBAN: <code>{dossier.bank?.iban || "—"}</code></p>
        <p>BIC: <code>{dossier.bank?.bic || "—"}</code></p>
        {staff ? (
          <form
            className="nd-form"
            onSubmit={(event) => {
              event.preventDefault();
              run(async () => {
                await saveNewcomerPaymentSettings({
                  ...bank,
                  membership_fee_cents: Math.round(Number(bank.membership_fee_cents) || 0),
                  session_fee_cents: Math.round(Number(bank.session_fee_cents) || 0),
                });
                return null;
              });
            }}
          >
            <label>
              FLTT form — club signatory name
              <input value={bank?.affiliation_signatory_name || ""} onChange={(event) => setBank({ ...bank, affiliation_signatory_name: event.target.value })} />
            </label>
            <label>
              FLTT form — signatory role
              <input value={bank?.affiliation_signatory_role || ""} onChange={(event) => setBank({ ...bank, affiliation_signatory_role: event.target.value })} />
            </label>
            <label>
              Account holder
              <input value={bank?.bank_account_holder || ""} onChange={(event) => setBank({ ...bank, bank_account_holder: event.target.value })} />
            </label>
            <label>
              IBAN
              <input value={bank?.bank_iban || ""} onChange={(event) => setBank({ ...bank, bank_iban: event.target.value })} />
            </label>
            <label>
              BIC
              <input value={bank?.bank_bic || ""} onChange={(event) => setBank({ ...bank, bank_bic: event.target.value })} />
            </label>
            <label>
              Membership amount in cents
              <input type="number" min="0" value={bank?.membership_fee_cents || 0} onChange={(event) => setBank({ ...bank, membership_fee_cents: event.target.value })} />
            </label>
            <label>
              Session amount in cents
              <input type="number" min="0" value={bank?.session_fee_cents || 0} onChange={(event) => setBank({ ...bank, session_fee_cents: event.target.value })} />
            </label>
            <button type="submit" disabled={busy}>Save club payment details</button>
          </form>
        ) : null}
        <div className="nd-actions">
          {staff ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => recordNewcomerPayment(dossier.dossier_id, {
                kind: "membership",
                method: "transfer",
                mark_received: true,
              }))}
            >
              Mark membership transfer received
            </button>
          ) : null}
          {dossier.stripe_ready ? (
            <button
              type="button"
              className="nd-btn--quiet"
              disabled={busy}
              onClick={() => run(async () => {
                const payload = await startNewcomerCardPayment({
                  dossierId: dossier.dossier_id,
                  token,
                  kind: "membership",
                });
                if (payload.data?.url) window.location.assign(payload.data.url);
                return payload;
              })}
            >
              Pay membership by card
            </button>
          ) : null}
        </div>
        {staff ? (
          <form
            className="nd-form"
            onSubmit={(event) => {
              event.preventDefault();
              run(() => saveNewcomerLicence(dossier.dossier_id, licence));
            }}
          >
            <label>
              Federation licence number
              <input value={licence} onChange={(event) => setLicence(event.target.value)} />
            </label>
            <button type="submit" disabled={busy}>Save licence number</button>
          </form>
        ) : null}
      </section>
      {onCreated ? null : null}
    </article>
  );

  if (staff) return shell;
  return <div className="nd-page">{shell}</div>;
}
